import { ReactNode } from "react";
import { FlexColumn, FlexRow } from "../Common/__styles__/Layout";
import { ScreenOnly } from "../Common/__styles__/ScreenOnly";
import { MarkdownTooltip } from "../Common/Tooltip";
import { Body } from "../Common/Typography";
import { FormControlWrapper } from "./__styles__/FormControl";

export type FormControlProps = {
  name: string;
  label?: Maybe<string>;
  required?: boolean;
  description?: Maybe<string>;
  helperText?: Maybe<string>;
  errorText?: Maybe<string>;
  tooltip?: string;
  children: ReactNode;
};

export const formControlIds = (name: string) => ({
  label: `${name}-label`,
  description: `${name}-description`,
  error: `${name}-error`,
  helper: `${name}-helper`,
});

export const formControlDescribedBy = (
  name: string,
  {
    description,
    helperText,
  }: Pick<FormControlProps, "description" | "helperText">,
) => {
  const ids = formControlIds(name);
  const parts = [
    description ? ids.description : null,
    helperText ? ids.helper : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : undefined;
};

export const formControlErrorMessageId = (
  name: string,
  errorText: FormControlProps["errorText"],
) => (errorText ? formControlIds(name).error : undefined);

export function FormControl({
  name,
  label,
  required,
  description,
  helperText,
  errorText,
  tooltip,
  children,
}: FormControlProps) {
  return (
    <FormControlWrapper>
      <FlexColumn gap="s">
        {label ? (
          <FormControl.LabelRow>
            <FormControl.Label htmlFor={name} required={required}>
              {label}
            </FormControl.Label>
            {tooltip ? <FormControl.Tooltip text={tooltip} /> : null}
          </FormControl.LabelRow>
        ) : null}
        {description ? (
          <FormControl.Description id={formControlIds(name).description}>
            {description}
          </FormControl.Description>
        ) : null}
        {children}
        {errorText || helperText ? (
          <FormControl.DetailsColumn>
            {errorText ? (
              <FormControl.ErrorText id={formControlIds(name).error}>
                {errorText}
              </FormControl.ErrorText>
            ) : null}
            {helperText ? (
              <FormControl.HelperText id={formControlIds(name).helper}>
                {helperText}
              </FormControl.HelperText>
            ) : null}
          </FormControl.DetailsColumn>
        ) : null}
      </FlexColumn>
    </FormControlWrapper>
  );
}

function FormControlLabelRow({ children }: { children: ReactNode }) {
  return (
    <FlexRow gap="s" style={{ alignItems: "center" }}>
      {children}
    </FlexRow>
  );
}

function FormControlLabel({
  required,
  htmlFor,
  children,
}: {
  required?: boolean;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} id={`${htmlFor}-label`}>
      <FlexRow gap="xs" style={{ alignItems: "center" }}>
        <Body size="default" type="emphasis" as="span">
          {children}
        </Body>
        {required ? (
          <Body
            size="default"
            type="emphasis"
            color="contentCritical"
            as="span"
          >
            <span aria-hidden="true">*</span>
          </Body>
        ) : null}
      </FlexRow>
    </label>
  );
}

function FormControlTooltip({ text }: { text: string }) {
  return <MarkdownTooltip tooltipText={text} place="right" />;
}

function FormControlDescription({
  id,
  children,
}: {
  id?: string;
  children: ReactNode;
}) {
  return (
    <ScreenOnly id={id}>
      <Body size="default" type="regular" color="contentSecondary">
        {children}
      </Body>
    </ScreenOnly>
  );
}

function FormControlDetailsColumn({ children }: { children: ReactNode }) {
  return <FlexColumn gap="xs">{children}</FlexColumn>;
}

function FormControlErrorText({
  id,
  children,
}: {
  id?: string;
  children: ReactNode;
}) {
  return (
    <span role="alert" id={id}>
      <Body size="small" type="regular" color="contentCritical" as="span">
        {children}
      </Body>
    </span>
  );
}

function FormControlHelperText({
  id,
  children,
}: {
  id?: string;
  children: ReactNode;
}) {
  return (
    <Body
      id={id}
      size="small"
      type="regular"
      color="contentSecondary"
      as="span"
    >
      {children}
    </Body>
  );
}

FormControl.LabelRow = FormControlLabelRow;
FormControl.Label = FormControlLabel;
FormControl.Tooltip = FormControlTooltip;
FormControl.Description = FormControlDescription;
FormControl.DetailsColumn = FormControlDetailsColumn;
FormControl.ErrorText = FormControlErrorText;
FormControl.HelperText = FormControlHelperText;
